import { BaseResource } from './base';
import {
  CheckIn,
  CreateCheckInDto,
  SearchCheckInsParams,
  GetCheckInStatsParams,
  CheckInStats,
  GetClientCheckInHistoryParams,
  CurrentlyInGymResponse,
  CheckInListResponse,
  ClientCheckInHistory,
} from '../models/check-ins';
import { RequestOptions, PaginatedResponseDto } from '../types';

export class CheckInsResource extends BaseResource {
  private basePath = 'check-ins';

  /**
   * Creates a new check-in for a client.
   *
   * **Permissions Required:**
   * - Gym owners with full gym access
   * - Active collaborators with CHECKINS_CREATE permission
   *
   * @param data - The check-in data including client ID and optional notes
   * @param options - Optional request configuration
   * @returns The created check-in record with client and user details
   * @throws {ValidationError} When client is not found or already checked in
   * @throws {AuthorizationError} When user lacks required permissions
   */
  async createCheckIn(data: CreateCheckInDto, options?: RequestOptions): Promise<CheckIn> {
    return this.client.post<CheckIn>(this.basePath, data, options);
  }

  async searchCheckIns(
    params?: SearchCheckInsParams,
    options?: RequestOptions,
  ): Promise<CheckInListResponse> {
    return this.client.get<CheckInListResponse>(this.basePath, params, options);
  }

  async getCurrentlyInGym(options?: RequestOptions): Promise<CurrentlyInGymResponse> {
    return this.client.get<CurrentlyInGymResponse>(`${this.basePath}/current`, undefined, options);
  }

  async getCheckIn(id: string, options?: RequestOptions): Promise<CheckIn> {
    return this.client.get<CheckIn>(`${this.basePath}/${id}`, undefined, options);
  }

  /**
   * Deletes a check-in record.
   *
   * **Permissions Required:**
   * - Gym owners with full gym access
   * - Active collaborators with CHECKINS_CREATE permission
   *
   * **Business Rules:**
   * - Only check-ins from today can be deleted
   * - The same permission level required for creation is required for deletion
   * - Cannot delete check-ins from previous days
   *
   * @param id - The ID of the check-in to delete
   * @param options - Optional request configuration
   * @returns Promise that resolves when the check-in is successfully deleted
   * @throws {NotFoundError} When the check-in is not found
   * @throws {AuthorizationError} When user lacks required permissions
   * @throws {ValidationError} When trying to delete a check-in from a previous day
   */
  async deleteCheckIn(id: string, options?: RequestOptions): Promise<void> {
    return this.client.delete<void>(`${this.basePath}/${id}`, options);
  }

  async getGymCheckInStats(
    params: GetCheckInStatsParams,
    options?: RequestOptions,
  ): Promise<CheckInStats> {
    return this.client.get<CheckInStats>(
      `${this.basePath}/stats/${params.period}`,
      undefined,
      options,
    );
  }

  async getClientCheckInHistory(
    clientId: string,
    params?: GetClientCheckInHistoryParams,
    options?: RequestOptions,
  ): Promise<ClientCheckInHistory> {
    return this.client.get<ClientCheckInHistory>(
      `${this.basePath}/client/${clientId}/history`,
      params,
      options,
    );
  }
}
